import React from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

/** AddFacilityPage / rapor paleti ile uyumlu */
const BRAND = '#094151';
const BRAND_HOVER = '#062a35';
const BRAND_MUTED_BG = 'rgba(9, 65, 81, 0.06)';
const BRAND_BORDER = 'rgba(9, 65, 81, 0.14)';

const BTN_CONTAINED_SX = {
    borderRadius: 2,
    textTransform: 'none' as const,
    fontWeight: 700,
    px: 2.25,
    py: 1,
    color: '#fff',
    bgcolor: BRAND,
    boxShadow: 'none',
    '&:hover': {
        bgcolor: BRAND_HOVER,
        boxShadow: 'none',
        color: '#fff',
    },
};

const BTN_OUTLINED_SX = {
    borderRadius: 2,
    textTransform: 'none' as const,
    fontWeight: 600,
    px: 2.25,
    py: 1,
    color: BRAND,
    borderColor: BRAND,
    bgcolor: '#fff',
    '&:hover': {
        borderColor: BRAND_HOVER,
        color: BRAND_HOVER,
        bgcolor: BRAND_MUTED_BG,
    },
};

const DIALOG_PAPER_SX = {
    borderRadius: 3,
    maxWidth: 440,
    width: '100%',
    m: 2,
    bgcolor: '#f8fbfc',
    backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #f4f9fa 100%)',
    border: `1px solid ${BRAND_BORDER}`,
    boxShadow: '0 12px 40px rgba(9, 65, 81, 0.12)',
} as const;

export interface GeolocationPermissionDialogsProps {
    softOpen: boolean;
    deniedOpen: boolean;
    onSoftConfirm: () => void;
    onSoftDismiss: () => void;
    /** Sadece kapat (backdrop / ESC / Kapat) */
    onDeniedDismiss: () => void;
    /**
     * Ayarı düzelttikten sonra konumu yeniden dener.
     * Web API site ayarları penceresini açamaz; yalnızca getCurrentPosition ile izin istemini tekrarlar.
     */
    onDeniedRetryLocation: () => void;
}

/**
 * Ön-izin (soft prompt): tarayıcı penceresini yalnızca birincil butonla tetikler.
 * Reddedildi yardımı: tarayıcıda izni nasıl açacağını anlatır.
 */
export const GeolocationPermissionDialogs: React.FC<GeolocationPermissionDialogsProps> = ({
    softOpen,
    deniedOpen,
    onSoftConfirm,
    onSoftDismiss,
    onDeniedDismiss,
    onDeniedRetryLocation,
}) => (
    <>
        <Dialog
            open={softOpen}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    onSoftDismiss();
                }
            }}
            aria-labelledby="geo-soft-title"
            PaperProps={{ sx: DIALOG_PAPER_SX }}
        >
            <DialogTitle
                id="geo-soft-title"
                sx={{
                    pt: 3,
                    px: 3,
                    pb: 1.5,
                    borderBottom: `1px solid ${BRAND_BORDER}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            bgcolor: BRAND_MUTED_BG,
                            border: `1px solid ${BRAND_BORDER}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <LocationSearchingIcon sx={{ fontSize: 30, color: BRAND }} />
                    </Box>
                    <Typography component="span" variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, color: BRAND }}>
                        Konumunuzu kullanalım mı?
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ px: 3, pt: 2.5, pb: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                    Tesisinizi haritada doğru gösterebilmemiz ve size en yakın hizmetleri sunabilmemiz için cihazınızın
                    konumuna ihtiyacımız var. Bir sonraki adımda tarayıcınız sizden izin isteyecek; yalnızca siz onay
                    verdiğinizde konum paylaşılır.
                </Typography>
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    py: 2.5,
                    gap: 1.25,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    borderTop: `1px solid ${BRAND_BORDER}`,
                    bgcolor: 'rgba(255,255,255,0.65)',
                }}
            >
                <Button variant="outlined" onClick={onSoftDismiss} sx={BTN_OUTLINED_SX}>
                    Şimdi değil
                </Button>
                <Button variant="contained" onClick={onSoftConfirm} sx={BTN_CONTAINED_SX}>
                    Konumumu kullan
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog
            open={deniedOpen}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    onDeniedDismiss();
                }
            }}
            aria-labelledby="geo-denied-title"
            PaperProps={{ sx: DIALOG_PAPER_SX }}
        >
            <DialogTitle
                id="geo-denied-title"
                sx={{
                    pt: 3,
                    px: 3,
                    pb: 1.5,
                    borderBottom: `1px solid ${BRAND_BORDER}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            bgcolor: 'rgba(211, 47, 47, 0.08)',
                            border: '1px solid rgba(211, 47, 47, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <SettingsOutlinedIcon sx={{ fontSize: 28, color: 'error.main' }} />
                    </Box>
                    <Typography component="span" variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, color: BRAND }}>
                        Konum izni kapalı görünüyor
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ px: 3, pt: 2.5, pb: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65, mb: 1.5 }}>
                    Endişelenmeyin — bunu tarayıcı ayarlarından birkaç saniyede açabilirsiniz. Konum paylaşımı yalnızca siz
                    izin verdiğinizde çalışır.
                </Typography>
                <Box
                    sx={{
                        borderRadius: 2,
                        p: 1.75,
                        mb: 1.5,
                        bgcolor: '#fff',
                        border: `1px solid ${BRAND_BORDER}`,
                    }}
                >
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.6, mb: 1 }}>
                        Site engellenmişken tarayıcı genelde üst çubukta küçük bir simge gösterir; büyük izin penceresi
                        açılmayabilir — bu normaldir. İzin menüsünü açmak için adres çubuğundaki bu simgeye tıklayın.
                        Site ayarlarını kod ile otomatik açmak güvenlik nedeniyle mümkün değildir. İzni açtıktan sonra
                        &quot;Tekrar dene&quot; ile isteği yenileyin.
                    </Typography>
                    <Box
                        component="ul"
                        sx={{
                            m: 0,
                            pl: 2.25,
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                            lineHeight: 1.7,
                        }}
                    >
                        <li>
                            Adres çubuğunun solundaki <strong>kilit veya bilgi</strong> simgesine tıklayın.
                        </li>
                        <li>
                            <strong>Konum</strong> veya <strong>İzinler</strong> bölümünden konumu &quot;İzin ver&quot; /{' '}
                            &quot;Allow&quot; yapın.
                        </li>
                        <li>Gerekirse sayfayı yenileyin; ardından &quot;Tekrar dene&quot;ye basın.</li>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    py: 2.5,
                    gap: 1.25,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    borderTop: `1px solid ${BRAND_BORDER}`,
                    bgcolor: 'rgba(255,255,255,0.65)',
                }}
            >
                <Button variant="outlined" onClick={onDeniedDismiss} sx={BTN_OUTLINED_SX}>
                    Kapat
                </Button>
                <Button variant="contained" onClick={onDeniedRetryLocation} sx={BTN_CONTAINED_SX}>
                    Tekrar dene
                </Button>
            </DialogActions>
        </Dialog>
    </>
);
